import React, { Component } from "react";
import { IbssPage } from "../../../../Components/Core/BasePage/IbssPage";
import { appContext } from "../../../../AppContext";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { Box, Grid, Typography } from "@mui/material";
import IbssSvgIcon from "../../../../Components/Icons/SvgIcon/IbssSvgIcon";
import IbssDataGrid from "../../../../Components/Data/DataGrid/IbssDataGrid";
import { GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import IbssActionButton, { IActionButton } from "../../../../Components/Buttons/ActionButton/IbssActionButton";
import Helper from "../../../../Common/Helper";
import RefreshIcon from "@mui/icons-material/Refresh";
import CreateIcon from "@mui/icons-material/Create";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import ConfirmModal from "../../../../Components/Dialogs/ConfirmDialog/ConfirmModal";
import IbssFilter from "../../../../Components/Forms/Filter/IbssFilter";
import { ApplicationType, SignPost } from "./DataModels";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditSignpostDrawer from "./EditSignpostDrawer";
import { QueryParams } from "./QueryParams";
import { rights } from "../../../../Common/RightsManager";
import IbssPageHeader from "../../../../Components/Headings/PageHeading/IbssPageHeader";

class ListSignposts extends IbssPage<IProps, IState, QueryParams>
{
    private get labels() { return appContext().labels; }
    private get appState() { return appContext().state; }
    private userCanUpdate: boolean;
    private userCanCreate: boolean;
    private userCanDelete: boolean;
    private columns: GridColDef<ISignpostView>[];

    constructor(props: IProps)
    {
        super(props, new QueryParams());
        this.userCanCreate = rights.DATAMODEL.BuildingKPIs.Create.has();
        this.userCanUpdate = rights.DATAMODEL.BuildingKPIs.Update.has();
        this.userCanDelete = rights.DATAMODEL.BuildingKPIs.Delete.has();
        this.state =
        {
            buildingId: this.appState.buildingId,
            isLoading: false,
            signpostList: [],
            openDrawer: false,
            selectedIds: [],
            disableActiveStatusButton: true,
            disableInActiveUpdateStatusButton: true,
            disableRefreshCache: false,
            showConfirmDialog: false,
            currentStatus: "active",
            selectedSignpostId: "",
            isEditMode: false,
            filterText: "",
            images: [],
        };
        this.columns = this.getColumns()
    }

    public async componentDidMount(): Promise<void>
    {
        await this.loadMedia();
        this.pageTitle = this.labels.HubMenuManage;
    }

    private async loadMedia(): Promise<void>
    {
        try
        {
            this.setState({ isLoading: true });
            const mediaFiles = await appContext().webEntryApiClientV1.api.webentry.v1.file.byPath.get<IFile[]>({
                path: 'c/Shortcuts'
            });
            const processedMedia = mediaFiles.map(item => ({
                ...item,
                path: `https://storage.ibss.${Helper.environment}/images/c/Shortcuts/${item.name}`
            }));
            this.setState({
                images: processedMedia,
                isLoading: false
            });
        }
        catch 
        {
            this.setState({ isLoading: false });
        }
    }

    private async uploadImage(filename: string, file: FormData, processAsMap: boolean): Promise<void>
    {
        try
        {
            this.setState({ isLoading: true });
            await appContext().webEntryApiClientV1.api.webentry.v1.file.byPath.post({
                path: 'c/Shortcuts/' + filename,
                processAsMapFile: processAsMap,
                body: file
            });
            await this.loadMedia();
        }
        catch
        {
            this.setState({ isLoading: false });
        }
    }

    public async queryParamsDidUpdate(): Promise<void>
    {
        const queryParams = this.queryParams
        if (!queryParams.building)
        {
            return this.pushQueryParams({ building: 1 }, true);
        }
        await this.setStateAsync({ buildingId: queryParams.building });
        await this.loadSignpostDetails();
    }

    private closeDrawer(): void
    {
        this.setState({
            openDrawer: false,
            selectedSignpostId: '',
            isEditMode: false
        });
    }

    private async loadSignpostDetails(): Promise<void>
    {
        try 
        {
            this.setState({ isLoading: true });
            const signpostList = await appContext().ibssApiClientV2.v2.byNodeid.shortcuts.get<SignPost[]>({
                nodeId: this.state.buildingId,
                select: SignPost,
                top: 100,
            });
            const signpostViewList = signpostList
                .filter((item) => item.ShortcutId)
                .map(item => ({
                    id: item.ShortcutId,
                    type: item.SignPostType,
                    displayName: item.SignPostType === 'KPI' ? item.Label : item.Heading,
                    applications: item.Application,
                    IsEnabled: item.IsEnabled
                }));

            this.selectedSignpostChanged(this.state.selectedIds);
            this.setState({ signpostList: signpostViewList });
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private renderUpdateIcon(signpost: ISignpostView): React.ReactNode
    {
        return (
            <IbssSvgIcon
                onClick={() =>
                    this.setState({
                        openDrawer: true,
                        selectedSignpostId: signpost.id,
                        isEditMode: true,
                    })
                }
            >
                <CreateIcon />
            </IbssSvgIcon>
        );
    }

    private async refreshCache(): Promise<void>
    {
        try
        {
            this.setState({ disableRefreshCache: true, isLoading: true });
            await appContext().ibssApiClientV2.v2.byNodeid.shortcuts.refresh.get({
                nodeId: this.state.buildingId,
            });

            setTimeout(() => this.setState({ disableRefreshCache: false }), 30000);
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async deleteSignposts(signpostIds: string[]): Promise<void>
    {
        try
        {
            this.setState({ isLoading: true });
            await Promise.all(
                signpostIds.map(signpostId =>
                    appContext().ibssApiClientV2.v2.byNodeid.shortcuts.byId.delete({
                        nodeId: this.state.buildingId,
                        id: signpostId,
                    })
                )
            );
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async enableSignPost(signpostIds: string[]): Promise<void>
    {
        try 
        {
            this.setState({ isLoading: true });
            await appContext().ibssApiClientV2.v2.byNodeid.shortcuts.enable.patch({
                nodeId: this.state.buildingId,
                body: {
                    ShortcutIds: signpostIds
                }
            });
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async disableSignPost(signpostIds: string[]): Promise<void>
    {
        try
        {
            this.setState({ isLoading: true });
            await appContext().ibssApiClientV2.v2.byNodeid.shortcuts.disable.patch({
                nodeId: this.state.buildingId,
                body: {
                    ShortcutIds: signpostIds
                }
            });
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private selectedSignpostChanged(signpostIds: GridRowSelectionModel): void
    {
        this.setState({ selectedIds: signpostIds });
        const selectedSignpostData = this.state.signpostList.filter(x => signpostIds.includes(x.id));
        const allActive = selectedSignpostData.every(x => x.IsEnabled === 1);
        const allInActive = selectedSignpostData.every(x => x.IsEnabled === 0);
        const hasMixedStatus = !allActive && !allInActive;

        this.setState({
            disableActiveStatusButton: signpostIds.length == 0 || allActive || hasMixedStatus || !this.userCanUpdate,
            disableInActiveUpdateStatusButton: signpostIds.length == 0 || allInActive || hasMixedStatus || !this.userCanUpdate
        });

    }

    private async updateSignpostStatus(status: 'active' | 'inActive' | 'cancel'): Promise<void>
    {
        try
        {
            this.setState({ isLoading: true });
            const selectedStringIds = this.state.selectedIds.map(id => String(id));
            if (status === 'active')
            {
                await this.enableSignPost(selectedStringIds);
                const updatedSignpostList = this.state.signpostList.map(signpost =>
                {
                    if (selectedStringIds.includes(signpost.id))
                    {
                        return { ...signpost, IsEnabled: 1 };
                    }
                    return signpost;
                });
                this.setState({
                    signpostList: updatedSignpostList,
                    disableActiveStatusButton: true,
                    disableInActiveUpdateStatusButton: false
                });
            }
            else if (status === 'inActive') 
            {
                await this.disableSignPost(selectedStringIds);
                const updatedSignpostList = this.state.signpostList.map(signpost =>
                {
                    if (selectedStringIds.includes(signpost.id))
                    {
                        return { ...signpost, IsEnabled: 0 };
                    }
                    return signpost;
                });
                this.setState({
                    signpostList: updatedSignpostList,
                    disableActiveStatusButton: false,
                    disableInActiveUpdateStatusButton: true
                });
            }
            else if (status === "cancel")
            {
                await this.deleteSignposts(selectedStringIds);
                const updatedSignpostList = this.state.signpostList.filter(
                    signpost => !selectedStringIds.includes(signpost.id)
                );
                this.setState({
                    signpostList: updatedSignpostList,
                    selectedIds: [],
                    disableActiveStatusButton: true,
                    disableInActiveUpdateStatusButton: true
                });
            }

            this.setState({ showConfirmDialog: false });
            await this.loadSignpostDetails();
        }
        finally
        {
            this.setState({ isLoading: false });
        }
    }

    private async showConfirmModal(status: 'active' | 'inActive' | 'cancel'): Promise<void>
    {
        this.setState({
            showConfirmDialog: true,
            currentStatus: status,
        });
    }

    private filterSignpost(searchTerm: string): ISignpostView[]
    {
        if (!searchTerm || searchTerm.trim() === "")
        {
            return this.state.signpostList;
        }

        const lowercaseSearchTerm = searchTerm.toLowerCase().trim();

        return this.state.signpostList.filter(signpost =>
        {
            const searchFields = [
                signpost.type,
                signpost.displayName,
                signpost.applications
            ];

            return searchFields.some(field =>
            {
                return field
                    ? field.toString().toLowerCase().trim().includes(lowercaseSearchTerm)
                    : false;
            });
        });
    }

    private getModalMessage(status: 'active' | 'inActive' | 'cancel')
    {
        switch (status)
        {
            case 'active':
                return this.labels.funcSignPostActiveConfirmMessage_L;
            case 'inActive':
                return this.labels.funcSignPostInactiveConfirmMessage_L;
            case 'cancel':
                return this.labels.funcSignPostDeleteConfirmMessage_L;
            default:
                return this.labels.funcInvalidStatusMessage_S;

        }
    }

    private getColumns(): GridColDef<ISignpostView>[]
    {
        return [
            {
                field: Helper.nameOf<ISignpostView>("type"),
                headerName: this.labels.funcType_S,
                flex: 1,
            },
            {
                field: Helper.nameOf<ISignpostView>("displayName"),
                headerName: this.labels.HubLabelDisplayName,
                flex: 1,
            },
            {
                field: Helper.nameOf<ISignpostView>("applications"),
                headerName: this.labels.funApplication_S,
                flex: 1,
            },
            {
                field: Helper.nameOf<ISignpostView>("IsEnabled"),
                headerName: this.labels.HubLabelStatus,
                flex: 1,
                renderCell: (params) => params.value === 1 ? this.labels.HubLabelActive : this.labels.HubLabelInActive,
            },
            {
                headerName: this.labels.HubLabelAction,
                minWidth: 120,
                field: "",
                flex: 1,
                filterable: false,
                sortable: false,
                renderCell: (params) => <>{this.renderUpdateIcon(params.row)}</>,
            },
        ];
    }

    private getActionButtons(): IActionButton[]
    {
        return [
            {
                label: this.labels.funcSpaceRefreshCache_S,
                icon: (
                    <IbssSvgIcon fontSize="small" className="mr-1">
                        <RefreshIcon />
                    </IbssSvgIcon>
                ),
                color: "info",
                onClick: () => this.refreshCache(),
                disabled: this.state.disableRefreshCache,
            },
            {
                label: this.labels.HubButtonDelete,
                icon: (
                    <IbssSvgIcon fontSize="small" className="mr-1">
                        <CancelIcon />
                    </IbssSvgIcon>
                ),
                color: "error",
                onClick: () => this.showConfirmModal("cancel"),
                disabled: this.state.selectedIds.length == 0 || !this.userCanDelete,
            },
            {
                label: this.labels.HubLabelActive,
                icon: (
                    <IbssSvgIcon fontSize='small' className="mr-1">
                        <CheckCircleIcon />
                    </IbssSvgIcon>
                ),
                color: "info",
                onClick: () => this.showConfirmModal("active"),
                disabled: this.state.disableActiveStatusButton 
            },
            {
                label: this.labels.HubLabelInActive,
                icon: (
                    <IbssSvgIcon fontSize='small' className="mr-1">
                        <CancelIcon />
                    </IbssSvgIcon>
                ),
                color: "error",
                onClick: () => this.showConfirmModal("inActive"),
                disabled: this.state.disableInActiveUpdateStatusButton,
            },
            {
                label: this.labels.HubButtonAdd,
                className: "addIcon",
                icon: (
                    <IbssSvgIcon>
                        <AddIcon />
                    </IbssSvgIcon>
                ),
                color: "primary",
                disabled: !this.userCanCreate,
                onClick: () => this.setState({
                    openDrawer: true,
                    selectedSignpostId: '',
                    isEditMode: false
                }),
            },
        ];
    }

    private handleConfirmOk(): void
    {
        this.setState({ showConfirmDialog: false });
        this.updateSignpostStatus(this.state.currentStatus);
    }
    
    private handleCloseConfirmModal() : void 
    {
        this.setState({ showConfirmDialog: false });
    }

    public render(): JSX.Element
    {

        const filteredSignpost = this.filterSignpost(this.state.filterText);

        return (
            <div className="page-height-exct-header">
                <div className="rightPanel-main-content">
                    <div className="table-panel">
                        <Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Box className="table-panel-header" style={{ paddingTop: "4px", width: "1000px" }} >
                                    <IbssPageHeader
                                            title={this.labels.Admin_Signposts_ListTitle}
                                            description={this.labels.Admin_Signposts_ListSubTitle}
                                        />
                                        <Box sx={{ width: "700px", mt: 3 }}>
                                            <IbssFilter
                                                searchTerm={this.state.filterText}
                                                onSearchTermChange={(event) => this.setState({ filterText: event.target.value })}
                                            />
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box component="div" sx={{ display: "flex", justifyContent: "right", alignItems: "center", my: 1, mr: 0, }}>
                                        <IbssActionButton buttons={this.getActionButtons()} />
                                    </Box>
                                    {
                                        <div>
                                            <IbssDataGrid
                                                loading={this.state.isLoading}
                                                className="view-signpost-data-grid"
                                                disableRowSelectionOnClick
                                                checkboxSelection
                                                columns={this.columns}
                                                onRowSelectionModelChange={(rowSelectionModel: GridRowSelectionModel) => this.selectedSignpostChanged(rowSelectionModel)}
                                                rows={filteredSignpost}
                                                pageSizeOptions={[100]}
                                            />
                                        </div>
                                    }
                                </Grid>
                            </Grid>
                        </Box>
                        <ConfirmModal
                            show={this.state.showConfirmDialog}
                            modalHeading={this.labels.HubLabelModalConfirmTitle}
                            modalMessage={this.getModalMessage(this.state.currentStatus)}
                            handleModal={() => this.handleCloseConfirmModal()}
                            okButton={() => this.handleConfirmOk()}
                        />
                        <EditSignpostDrawer
                            buildingId={this.state.buildingId}
                            selectedSignpostId={this.state.selectedSignpostId}
                            open={this.state.openDrawer}
                            onClose={() => this.closeDrawer()}
                            loadSignpostDetails={() => this.loadSignpostDetails()}
                            selectedIdChanged={(id: string) => this.setState({ selectedSignpostId: id })}
                            isEditMode={this.state.isEditMode}
                            images={this.state.images}
                            onImageUpload={(filename: string, file: FormData, processAsMap: boolean) =>
                                this.uploadImage(filename, file, processAsMap)}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default withRouter(ListSignposts);

export interface IProps extends RouteComponentProps { }

export interface IState
{
    buildingId: number;
    isLoading: boolean;
    signpostList: ISignpostView[];
    openDrawer: boolean;
    disableRefreshCache: boolean;
    selectedIds: GridRowSelectionModel;
    disableActiveStatusButton: boolean;
    disableInActiveUpdateStatusButton: boolean;
    showConfirmDialog: boolean;
    currentStatus: 'active' | 'inActive' | 'cancel';
    selectedSignpostId: string;
    isEditMode: boolean;
    filterText: string;
    images: IFile[];
}
export interface IFile
{
    name: string;
    path: string;
    lastModified: string;
    createdAt: string;
    size: string;
}

export interface ISignpostView
{
    id: string;
    type: string;
    displayName: string;
    applications: ApplicationType[];
    IsEnabled: number;
}
